export interface PresidentialArchiveProfile {
  name: string;
  slug: string;
  sourceUrl: string;
  photoUrl: string;
  /** Archive entries that do not represent an individual who assumed the presidency. */
  comparable: boolean;
}

const nonOfficeholderSlugs = new Set([
  '192-junta-militar',
  '202-tancredo-de-almeida-neves',
  '217-julio-prestes-de-albuquerque',
  '219-junta-governativa',
]);

const officialPortraits: Record<string, string> = {
  '178-afonso-augusto-moreira-pena': 'BR_RJANRIO_ON_0_FOT_0061_m0001de0001_Afonso_Pena.png',
  '189-artur-da-costa-e-silva': 'Costa%20e%20Silva.png',
  '207-artur-da-silva-bernardes': '10.arthur_da_silva_bernardes.png',
  '195-carlos-coimbra-da-luz': 'Carlos_Luz.png',
  '206-delfim-moreira-da-costa-ribeiro': '9.delfim_moreira.png',
  '205-dilma-vana-rousseff': 'Dilma%20Rousseff.png',
  '190-emilio-garrastazu-medici': 'Medici.png',
  '186-epitacio-lindolfo-da-silva-pessoa': 'BR_RJANRIO_PH_0_FOT_38683_003_Epitacio_Pessoa.png',
  '183-ernesto-geisel': 'BR_RJANRIO_PH_0_FOT_23314_001_Geisel.png',
  '181-eurico-gaspar-dutra': 'BR_RJANRIO_EH_0_FOT_PRP_03464_mp0018de0067_Eurico_Gaspar_Dutra.png',
  '200-fernando-afonso-collor-de-mello': 'collor%20retrato1.png',
  '197-fernando-henrique-cardoso': 'FHC%202.png',
  '213-floriano-vieira-peixoto': '2.floriano_peixoto.png',
  '215-francisco-de-paula-rodrigues-alves': 'Rodrigues_Alves.png',
  '179-getulio-dorneles-vargas': 'BR_RJANRIO_EH_0_FOT_PRP_03166_mp0007de0029%20Getulio%20Vargas%202%20governo.png',
  '209-hermes-rodrigues-da-fonseca': '7.hermes_da_fonseca.png',
  '196-humberto-de-alencar-castelo-branco': 'Castelo%20Branco.png',
  '203-itamar-augusto-cautiero-franco': 'itamar%20retrato1.png',
  '199-jair-messias-bolsonaro': 'bolsonaro.png',
  '185-janio-da-silva-quadros': 'BR_RJANRIO_PH_0_FOT_00163_m0184de0186_Janio.png',
  '193-joao-batista-de-oliveira-figueiredo': 'Figueiredo.png',
  '191-joao-belchior-marques-goulart': 'Joao%20Goulart.png',
  '188-joao-cafe-filho': 'Cafe_Filho.png',
  '180-jose-linhares': 'BR_RJANRIO_EH_0_FOT_PRP_03304_mp0002de0004_Jose%20Linhares.png',
  '198-jose-sarney': 'sarney%20retrato1.png',
  '217-julio-prestes-de-albuquerque': 'Julio_Prestes.png',
  '219-junta-governativa': 'Junta_Governativa.png',
  '192-junta-militar': 'Junta_Militar.png',
  '182-juscelino-kubitschek-de-oliveira': 'BR_RJANRIO_EH_0_FOT_PRP_05522_mp0011de0032_Juscelino.png',
  '204-luiz-inacio-lula-da-silva': 'lula3.png',
  '212-manuel-deodoro-da-fonseca': '1.manuel_deodoro_da_fonseca.png',
  '214-manuel-ferraz-de-campos-sales': 'Campos_Sales.png',
  '201-michel-miguel-elias-temer-lulia': 'temer%201.png',
  '194-nereu-de-oliveira-ramos': 'Nereu_Ramos.png',
  '210-nilo-procopio-pecanha': '6.nilo_pecanha.png',
  '184-pascoal-ranieri-mazzilli': 'ranieiri.png',
  '216-prudente-jose-de-morais-barros': 'Prudente_de_Morais.png',
  '202-tancredo-de-almeida-neves': 'tancredo.png',
  '211-venceslau-bras-pereira-gomes': '8.venceslau_bras.png',
  '208-washington-luis-pereira-de-souza': '11.washington_luis.png',
};

export const presidentialProfiles: PresidentialArchiveProfile[] = ([
  ['Afonso Augusto Moreira Pena', '178-afonso-augusto-moreira-pena'],
  ['Artur da Costa e Silva', '189-artur-da-costa-e-silva'],
  ['Artur da Silva Bernardes', '207-artur-da-silva-bernardes'],
  ['Carlos Coimbra da Luz', '195-carlos-coimbra-da-luz'],
  ['Delfim Moreira da Costa Ribeiro', '206-delfim-moreira-da-costa-ribeiro'],
  ['Dilma Vana Rousseff', '205-dilma-vana-rousseff'],
  ['Emílio Garrastazu Médici', '190-emilio-garrastazu-medici'],
  ['Epitácio Lindolfo da Silva Pessoa', '186-epitacio-lindolfo-da-silva-pessoa'],
  ['Ernesto Geisel', '183-ernesto-geisel'],
  ['Eurico Gaspar Dutra', '181-eurico-gaspar-dutra'],
  ['Fernando Afonso Collor de Mello', '200-fernando-afonso-collor-de-mello'],
  ['Fernando Henrique Cardoso', '197-fernando-henrique-cardoso'],
  ['Floriano Vieira Peixoto', '213-floriano-vieira-peixoto'],
  ['Francisco de Paula Rodrigues Alves', '215-francisco-de-paula-rodrigues-alves'],
  ['Getúlio Dornelles Vargas', '179-getulio-dorneles-vargas'],
  ['Hermes Rodrigues da Fonseca', '209-hermes-rodrigues-da-fonseca'],
  ['Humberto de Alencar Castelo Branco', '196-humberto-de-alencar-castelo-branco'],
  ['Itamar Augusto Cautiero Franco', '203-itamar-augusto-cautiero-franco'],
  ['Jair Messias Bolsonaro', '199-jair-messias-bolsonaro'],
  ['Jânio da Silva Quadros', '185-janio-da-silva-quadros'],
  ['João Batista de Oliveira Figueiredo', '193-joao-batista-de-oliveira-figueiredo'],
  ['João Belchior Marques Goulart', '191-joao-belchior-marques-goulart'],
  ['João Café Filho', '188-joao-cafe-filho'],
  ['José Linhares', '180-jose-linhares'],
  ['José Sarney', '198-jose-sarney'],
  ['Júlio Prestes de Albuquerque', '217-julio-prestes-de-albuquerque'],
  ['Junta Governativa', '219-junta-governativa'],
  ['Junta Militar', '192-junta-militar'],
  ['Juscelino Kubitschek de Oliveira', '182-juscelino-kubitschek-de-oliveira'],
  ['Luiz Inácio Lula da Silva', '204-luiz-inacio-lula-da-silva'],
  ['Manuel Deodoro da Fonseca', '212-manuel-deodoro-da-fonseca'],
  ['Manuel Ferraz de Campos Sales', '214-manuel-ferraz-de-campos-sales'],
  ['Michel Miguel Elias Temer Lulia', '201-michel-miguel-elias-temer-lulia'],
  ['Nereu de Oliveira Ramos', '194-nereu-de-oliveira-ramos'],
  ['Nilo Procópio Peçanha', '210-nilo-procopio-pecanha'],
  ['Pascoal Ranieri Mazzilli', '184-pascoal-ranieri-mazzilli'],
  ['Prudente José de Morais Barros', '216-prudente-jose-de-morais-barros'],
  ['Tancredo de Almeida Neves', '202-tancredo-de-almeida-neves'],
  ['Venceslau Brás Pereira Gomes', '211-venceslau-bras-pereira-gomes'],
  ['Washington Luís Pereira de Souza', '208-washington-luis-pereira-de-souza'],
] as Array<[string, string]>).map(([name, slug]) => ({
  name,
  slug,
  sourceUrl: `https://presidentes.an.gov.br/index.php/centro-de-referencia-de-acervos-presidenciais/assuntos/biografias/${slug}`,
  photoUrl: `https://presidentes.an.gov.br/images/biografias/${officialPortraits[slug]}`,
  comparable: !nonOfficeholderSlugs.has(slug),
}));

export function getPresidentialProfile(slug: string | undefined) {
  return presidentialProfiles.find((profile) => profile.slug === slug) ?? null;
}

export function isPresidentialOfficeholder(profile: PresidentialArchiveProfile) {
  return profile.comparable;
}
